import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import BRAND from '../config/brand';
import { CONTENT_CATEGORIES, GOAL_SETTING_MEETING } from '../config/planningOffers';
import { buildCarLeaseBuyProjection, DEFAULT_CAR_INPUTS } from '../lib/carLeaseBuyCalculator';
import CalculatorDiscoveryCta from './CalculatorDiscoveryCta';
import ServiceNextStep from './ServiceNextStep';
import './IncomeChangePaycheckTaxEstimator.css';
import './HousingDecisionCalculator.css';
import './CarLeaseBuyCalculator.css';

const PAGE_TITLE = 'Car Lease vs Buy Calculator | BPA';
const META_DESCRIPTION =
  "Compare leasing vs buying a car with BPA's calculator — monthly payments, fees, financing costs, vehicle equity, total net cost, and the estimated break-even year.";

const EDUCATIONAL_DISCLOSURE =
  'Dealer quotes vary in how taxes and fees are charged. Confirm every figure on the lease worksheet or purchase order.';

const RESULTS_NOTE =
  'This estimate excludes costs assumed similar unless entered, such as insurance, fuel, parking, and repairs. Excess-mileage charges, excess-wear charges, incentives, rebates, lease-end purchases, and investment returns on unused cash can materially change the outcome. Results apply only to the assumptions you enter — neither option is universally better.';

const CALCULATOR_ID = 'car_lease_buy_calculator';
const CALCULATOR_NAME = 'car_lease_buy';

const GROUPS = [
  {
    title: 'Vehicle & comparison',
    fields: [
      ['vehiclePrice', 'Vehicle price', '1000'],
      ['salesTaxRate', 'Sales tax rate (%)', '0.1'],
      ['comparisonYears', 'Years to compare', '1'],
      ['annualDepreciationRate', 'Estimated annual depreciation (%)', '0.5'],
    ],
  },
  {
    title: 'Buying',
    fields: [
      ['buyDownPayment', 'Down payment', '500'],
      ['loanApr', 'Loan APR (%)', '0.1'],
      ['loanTermMonths', 'Loan term (months)', '12'],
      ['buyDocFee', 'Documentation fee', '50'],
      ['buyRegistrationFee', 'Registration & title fees', '50'],
      ['annualMaintenanceBuy', 'Estimated annual maintenance', '100'],
    ],
  },
  {
    title: 'Leasing',
    fields: [
      ['leaseDownPayment', 'Lease down payment / cap cost reduction', '500'],
      ['leaseMoneyFactor', 'Money factor', '0.0001'],
      ['leaseTermMonths', 'Lease term (months)', '12'],
      ['leaseResidualRate', 'Residual value (%)', '1'],
      ['leaseAcquisitionFee', 'Acquisition fee', '50'],
      ['leaseDispositionFee', 'Disposition fee', '50'],
      ['leaseDocFee', 'Documentation fee', '50'],
      ['leaseRegistrationFee', 'Registration & title fees', '50'],
      ['annualMaintenanceLease', 'Estimated annual maintenance', '100'],
    ],
  },
];

function formatUsd(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatUsd2(value) {
  if (value == null || !Number.isFinite(value)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 2,
  }).format(value);
}

function usePageSeo(title, description) {
  useEffect(() => {
    const previousTitle = document.title;
    document.title = title;
    let meta = document.querySelector('meta[name="description"]');
    const created = !meta;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const previousDescription = meta.getAttribute('content');
    meta.setAttribute('content', description);
    return () => {
      document.title = previousTitle;
      if (created) meta.remove();
      else if (previousDescription != null) meta.setAttribute('content', previousDescription);
    };
  }, [title, description]);
}

export default function CarLeaseBuyCalculator() {
  const {
    trackComplete,
    trackFieldInteraction,
    trackReset,
    trackResultView,
  } = useCalculatorAnalytics({
    calculatorId: CALCULATOR_ID,
    calculatorName: CALCULATOR_NAME,
  });

  usePageSeo(PAGE_TITLE, META_DESCRIPTION);

  const [values, setValues] = useState(() => ({ ...DEFAULT_CAR_INPUTS }));
  const projection = useMemo(() => buildCarLeaseBuyProjection(values), [values]);

  useEffect(() => {
    if (!projection) return;
    const params = { result_category: projection.winner };
    trackResultView(params);
    trackComplete(params);
  }, [projection, trackComplete, trackResultView]);

  const updateField = (key) => (value) => {
    trackFieldInteraction(key);
    setValues((current) => ({ ...current, [key]: value }));
  };

  const resetCalculator = () => {
    setValues({ ...DEFAULT_CAR_INPUTS });
    trackReset();
  };

  return (
    <div className="pete-page hdc-page car-calc-page">
      <header className="pete-header">
        <div className="pete-header-inner">
          <Link className="pete-logo-link" to="/links">
            <img
              src={BRAND.logoSrc}
              alt="Bautista Planning and Analytics"
              className="pete-logo-img"
              width={440}
              height={88}
              decoding="async"
            />
          </Link>
        </div>
      </header>

      <main className="pete-body">
        <div className="pete-inner hdc-inner">
          <div className="pete-title-block hdc-hero">
            <p className="hdc-brand-path">Clear Thinking → Better Decisions → The Golden Path</p>
            <h1>Car Lease vs Buy Calculator</h1>
            <p className="hdc-hero-sub">
              Look beyond the advertised payment. Compare fees, financing costs, vehicle equity, total net cost,
              and the year buying may pull ahead.
            </p>
          </div>

          <div className="pete-disclaimer-banner hdc-disclaimer" role="note">
            <strong>Educational estimate only.</strong> {EDUCATIONAL_DISCLOSURE}
          </div>

          <div className="car-calc-layout">
            <section className="pete-card car-inputs" aria-label="Calculator assumptions">
              <div className="car-card-heading">
                <div>
                  <p className="car-kicker">Your assumptions</p>
                  <h2>Build the comparison</h2>
                </div>
                <button type="button" className="car-reset" onClick={resetCalculator}>
                  Reset
                </button>
              </div>

              {GROUPS.map((group) => (
                <fieldset key={group.title} className="car-fieldset">
                  <legend>{group.title}</legend>
                  <div className="car-field-grid">
                    {group.fields.map(([key, label, step]) => (
                      <Field
                        key={key}
                        id={`car-${key}`}
                        label={label}
                        step={step}
                        value={values[key]}
                        onChange={updateField(key)}
                      />
                    ))}
                  </div>
                </fieldset>
              ))}
            </section>

            <section className="car-results" aria-live="polite" aria-atomic="false">
              {!projection ? (
                <div className="pete-card car-error" role="alert">
                  <h2>Check your assumptions</h2>
                  <p>
                    Vehicle price must be greater than zero. Down payments cannot exceed the vehicle price.
                    Loan and lease terms must be greater than zero. Residual value cannot exceed 100%, and
                    depreciation must be under 100%. Fees, rates, and maintenance cannot be negative.
                  </p>
                </div>
              ) : (
                <>
                  <RecommendationCard projection={projection} />

                  <div className="car-summary-grid">
                    <SummaryCard
                      title="Buy"
                      marker="B"
                      payment={projection.buy.monthly}
                      cost={projection.final.buyNetCost}
                      rate={`${Number(values.loanApr).toFixed(2)}% APR`}
                      years={projection.years}
                    />
                    <SummaryCard
                      title="Lease"
                      marker="L"
                      payment={projection.lease.monthly}
                      cost={projection.final.leaseNetCost}
                      rate={`${projection.lease.equivalentApr.toFixed(2)}% approx. APR equiv.`}
                      years={projection.years}
                    />
                  </div>

                  <div className="pete-card">
                    <h2>Fees &amp; financing</h2>
                    <div className="car-detail-columns">
                      <DetailColumn
                        title="Buy"
                        rows={[
                          ['Amount financed', formatUsd(projection.buy.amountFinanced)],
                          ['Sales tax', formatUsd(projection.buy.tax)],
                          ['Documentation & registration fees', formatUsd(projection.buy.fees)],
                          ['Total loan interest', formatUsd(projection.buy.financeCharge)],
                          ['Loan APR', `${Number(values.loanApr).toFixed(2)}%`],
                        ]}
                      />
                      <DetailColumn
                        title="Lease"
                        rows={[
                          ['Adjusted capitalized cost', formatUsd(projection.lease.adjustedCapCost)],
                          ['Acquisition, documentation & registration', formatUsd(projection.lease.startFees)],
                          ['Residual value', formatUsd(projection.lease.residualValue)],
                          ['Rent charge over one lease', formatUsd(projection.lease.financeCharge)],
                          ['Money factor', String(values.leaseMoneyFactor)],
                          [
                            'Approximate APR equivalent',
                            `${projection.lease.equivalentApr.toFixed(2)}%`,
                          ],
                          ['Disposition fee', formatUsd(projection.lease.dispositionFee)],
                        ]}
                      />
                    </div>
                    <p className="car-note">
                      Money factor × 2,400 gives the commonly used approximate APR equivalent. It is an
                      approximation, not a disclosed loan APR.
                    </p>
                  </div>

                  <div className="pete-card">
                    <h2>Cost over time</h2>
                    <p className="car-note">
                      Purchase net cost subtracts estimated vehicle equity (vehicle value minus remaining loan
                      balance, floored at zero). For horizons longer than one lease term, another lease with the
                      same terms is assumed, including upfront and disposition fees each cycle.
                    </p>
                    <CostChart rows={projection.rows} breakEvenYear={projection.breakEvenYear} />
                    <div className="car-table-wrap">
                      <table className="car-table">
                        <caption className="car-table-caption">
                          Year-by-year net cost comparison
                          {projection.breakEvenYear
                            ? `. Break-even marked in year ${projection.breakEvenYear}.`
                            : '. No break-even within this period.'}
                        </caption>
                        <thead>
                          <tr>
                            <th scope="col">Year</th>
                            <th scope="col">Buy net cost</th>
                            <th scope="col">Lease net cost</th>
                            <th scope="col">Difference</th>
                            <th scope="col">Lower-cost option</th>
                          </tr>
                        </thead>
                        <tbody>
                          {projection.rows.map((row) => {
                            const isBreakEven = row.year === projection.breakEvenYear;
                            return (
                              <tr
                                key={row.year}
                                className={isBreakEven ? 'car-break-even-row' : undefined}
                              >
                                <td>
                                  {row.year}
                                  {isBreakEven ? (
                                    <span className="car-be-marker" title="Break-even year">
                                      {' '}
                                      ★ Break-even
                                    </span>
                                  ) : null}
                                </td>
                                <td>{formatUsd(row.buyNetCost)}</td>
                                <td>{formatUsd(row.leaseNetCost)}</td>
                                <td>
                                  {row.difference >= 0
                                    ? `Buy lower by ${formatUsd(row.difference)}`
                                    : `Lease lower by ${formatUsd(Math.abs(row.difference))}`}
                                </td>
                                <td>
                                  {row.lowerCostOption === 'buy' ? 'Buy' : 'Lease'}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div className="pete-disclaimer-banner hdc-disclaimer" role="note">
                    <strong>About these results.</strong> {RESULTS_NOTE}
                  </div>

                  <CalculatorDiscoveryCta
                    className="rtc-cta"
                    eyebrow="NEXT STEP"
                    headline="Want help deciding whether to lease or buy?"
                    body={[
                      'This calculator uses simplified assumptions. A comprehensive financial plan reviews the lease vs buy decision with your cash flow, tax situation, how long you keep vehicles, and what else that money could do.',
                      `Start with a ${GOAL_SETTING_MEETING.name}: bring the result from this page and we’ll talk through your goals and how a plan would approach the decision.`,
                    ]}
                    buttonText={GOAL_SETTING_MEETING.cta}
                    buttonId="car_lease_buy_calculator_financial_checkup_cta"
                    secondaryButtonId="car_lease_buy_calculator_send_result_cta"
                    calculatorId={CALCULATOR_ID}
                    calculatorName={CALCULATOR_NAME}
                    postButtonLine={RESULTS_NOTE}
                  />
                  <ServiceNextStep
                    contentCategory={CONTENT_CATEGORIES.GENERAL}
                    calculatorId={CALCULATOR_ID}
                    calculatorName={CALCULATOR_NAME}
                    promotionalAngle="car_lease_buy_calculator"
                  />

                  <p className="pete-footer-note">
                    <Link className="pete-portal-link" to="/calculators">
                      All planning calculators
                    </Link>
                    {' · '}
                    <Link className="pete-portal-link" to="/links">
                      Back to planning links
                    </Link>
                  </p>
                </>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

function RecommendationCard({ projection }) {
  const { winner, years, savings, breakEvenYear } = projection;
  const headline =
    winner === 'buy'
      ? `Buying comes out ahead at year ${years}.`
      : `Leasing costs less at year ${years}.`;

  const breakEvenText = breakEvenYear
    ? `Buying first becomes the lower-cost path in year ${breakEvenYear}.`
    : 'Buying does not reach break-even within this comparison period.';

  return (
    <div className={`pete-card car-verdict car-verdict--${winner}`}>
      <p className="car-kicker">Recommendation summary</p>
      <h2>{headline}</h2>
      <div className="car-savings">
        {formatUsd(savings)}
        <span>Estimated difference</span>
      </div>
      <p>
        Selected comparison year: {years}. {breakEvenText}
      </p>
      <p className="car-verdict-caveat">
        Based only on the assumptions entered — not a universal recommendation.
      </p>
    </div>
  );
}

function Field({ id, label, value, onChange, step }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        min="0"
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function SummaryCard({ title, marker, payment, cost, rate, years }) {
  return (
    <div className={`pete-card car-summary car-summary--${title.toLowerCase()}`}>
      <div className="car-summary-title">
        <span className="car-marker" aria-hidden="true">
          {marker}
        </span>
        {title}
      </div>
      <strong>
        {formatUsd2(payment)}
        <span>/mo</span>
      </strong>
      <p>{rate}</p>
      <div className="car-summary-total">
        <span>Net cost at year {years}</span>
        <b>{formatUsd(cost)}</b>
      </div>
    </div>
  );
}

function DetailColumn({ title, rows }) {
  return (
    <div className="car-detail">
      <h3>{title}</h3>
      {rows.map(([label, value]) => (
        <div key={label}>
          <span>{label}</span>
          <strong>{value}</strong>
        </div>
      ))}
    </div>
  );
}

function CostChart({ rows, breakEvenYear }) {
  const max = Math.max(...rows.flatMap((r) => [r.buyNetCost, r.leaseNetCost]), 1);

  return (
    <div
      className="car-chart"
      role="img"
      aria-label={`Buy and lease net cost by year${
        breakEvenYear ? `, with break-even at year ${breakEvenYear}` : ', with no break-even in this period'
      }`}
    >
      {rows.map((row) => {
        const isBreakEven = row.year === breakEvenYear;
        return (
          <div
            className={`car-chart-year${isBreakEven ? ' car-chart-year--break-even' : ''}`}
            key={row.year}
          >
            <span className="car-chart-year-label">
              Y{row.year}
              {isBreakEven ? <span className="car-be-marker"> ★</span> : null}
            </span>
            <div className="car-chart-bars">
              <div className="car-bar-row">
                <span className="car-bar-tag">Buy</span>
                <i
                  className="car-bar car-bar--buy"
                  style={{ width: `${Math.max(2, (row.buyNetCost / max) * 100)}%` }}
                  title={`Buy ${formatUsd(row.buyNetCost)}`}
                />
                <span className="car-bar-value">{formatUsd(row.buyNetCost)}</span>
              </div>
              <div className="car-bar-row">
                <span className="car-bar-tag">Lease</span>
                <i
                  className="car-bar car-bar--lease"
                  style={{ width: `${Math.max(2, (row.leaseNetCost / max) * 100)}%` }}
                  title={`Lease ${formatUsd(row.leaseNetCost)}`}
                />
                <span className="car-bar-value">{formatUsd(row.leaseNetCost)}</span>
              </div>
            </div>
          </div>
        );
      })}
      <div className="car-chart-legend">
        <span>
          <i className="car-dot car-dot--buy" aria-hidden="true" />
          Buy (solid)
        </span>
        <span>
          <i className="car-dot car-dot--lease" aria-hidden="true" />
          Lease (hatched)
        </span>
        {breakEvenYear ? (
          <span>
            <span className="car-be-marker" aria-hidden="true">
              ★
            </span>{' '}
            Break-even year
          </span>
        ) : null}
      </div>
    </div>
  );
}
